/**
 * GIWAHS — Supplier verification page at /supplier/verification
 * ---------------------------------------------------------------------
 * Single-page flow for an authenticated supplier to:
 *   1. See current tier + computed trust score
 *   2. Upload required documents per tier
 *   3. Submit a verification request for review
 *   4. Track in_review / approved / rejected status
 *
 * All RPCs are SECURITY DEFINER; only the supplier's owner (supplier_users)
 * can act on their own supplier record.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Upload, Check, X, AlertCircle, Award, FileText, ArrowRight, Clock, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { supplierClient } from '../lib/supplierClient';
import { paymentsClient } from '../lib/paymentsClient';
import { useAuth } from '../context/AuthContext';
import { track, EVENTS } from '../lib/analytics';

const DOC_TYPES = [
  { key: 'business_registration', label: 'Business registration', required: ['verified', 'gold', 'elite'] },
  { key: 'tax_vat',               label: 'Tax / VAT ID',           required: ['verified', 'gold', 'elite'] },
  { key: 'factory_address_proof', label: 'Factory address proof',   required: ['verified', 'gold', 'elite'] },
  { key: 'production_photos',     label: 'Production-floor photos (≥3)', required: ['verified', 'gold', 'elite'], multi: true },
  { key: 'compliance_cert',       label: 'Compliance certifications (BSCI, WRAP, GOTS, ISO…)', required: ['gold', 'elite'], multi: true, recommendedFor: ['verified'] },
  { key: 'capacity_attestation',  label: 'Capacity attestation form', required: ['gold', 'elite'] },
  { key: 'buyer_reference',       label: 'Buyer reference letter / past PO', required: ['gold', 'elite'], multi: true },
  { key: 'audit_report',          label: 'Recent third-party audit report (≤12 months)', required: ['elite'], recommendedFor: ['gold'] },
  { key: 'on_site_visit_report',  label: 'On-site visit report (GIWAHS or partner)', required: ['elite'], internalOnly: true },
];

const TIER_OPTIONS = [
  { key: 'verified', label: 'Verified', price: 'Free' },
  { key: 'gold',     label: 'Premium Gold', price: 'NZ$490 / year' },
  { key: 'elite',    label: 'Enterprise Elite', price: 'NZ$1,490 / year' },
];

const PAID_TIERS = new Set(['gold', 'elite']);

const STATUS_TONE = {
  unverified: 'border-neutral-300 bg-neutral-50 text-neutral-700',
  in_review:  'border-amber-500 bg-amber-50 text-amber-900',
  verified:   'border-emerald-700 bg-emerald-50 text-emerald-900',
  rejected:   'border-red-500 bg-red-50 text-red-900',
  expired:    'border-neutral-400 bg-neutral-100 text-neutral-700',
  revoked:    'border-red-700 bg-red-100 text-red-900',
};

const SupplierVerification = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [data, setData] = useState(null);
  const [requestedTier, setRequestedTier] = useState('verified');
  const [attestation, setAttestation] = useState('');
  const [uploadingType, setUploadingType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [payingRequestId, setPayingRequestId] = useState(null);
  const [pollingSession, setPollingSession] = useState(null);
  const [paymentBanner, setPaymentBanner] = useState(null); // { ok, text }

  const supplierSlug = data?.supplier?.slug;

  const load = useCallback(async () => {
    setErr('');
    try {
      const me = await supplierClient.mySupplierId();
      if (!me) {
        setErr('No supplier account found for this user. Apply or claim a listing first.');
        setLoading(false);
        return;
      }
      // mySupplierId returns the supplier ID; we need the slug to call myVerification.
      // Fetch the supplier row.
      const client = await import('../lib/supabaseClient');
      const sb = client.getSupabaseClient();
      const { data: row } = await sb.from('suppliers').select('slug').eq('id', me).maybeSingle();
      if (!row?.slug) {
        setErr('Supplier slug not found.');
        setLoading(false);
        return;
      }
      const result = await supplierClient.myVerification(row.slug);
      setData(result);
      track(EVENTS.SUPPLIER_VERIFICATION_STARTED, { supplier_slug: row.slug });
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login?next=/supplier/verification'); return; }
    load();
  }, [authLoading, user, navigate, load]);

  const handleUpload = async (doc_type, file) => {
    if (!supplierSlug) return;
    setUploadingType(doc_type);
    try {
      const latestReq = (data?.requests || [])[0];
      const request_id = latestReq?.status === 'in_review' ? latestReq.id : null;
      await supplierClient.uploadDocument({
        supplier_slug: supplierSlug,
        request_id,
        doc_type,
        file,
      });
      toast.success(`Uploaded ${file.name}`);
      await load();
    } catch (e) {
      toast.error(`Upload failed: ${e?.message || e}`);
    } finally {
      setUploadingType(null);
    }
  };

  const submitForReview = async () => {
    if (!supplierSlug) return;
    setSubmitting(true);
    try {
      await supplierClient.submitVerification({
        supplier_slug: supplierSlug,
        requested_tier: requestedTier,
        attestation: attestation || null,
      });
      toast.success('Verification request submitted. Our team will review within 2–3 business days.');
      setAttestation('');
      await load();
    } catch (e) {
      toast.error(`Could not submit: ${e?.message || e}`);
    } finally {
      setSubmitting(false);
    }
  };

  const startPayment = async (request) => {
    if (!supplierSlug || !request?.id) return;
    setPayingRequestId(request.id);
    track(EVENTS.SUPPLIER_VERIFICATION_PAY_STARTED, { tier: request.requested_tier, request_id: request.id });
    try {
      const { url } = await paymentsClient.createCheckout({
        tier: request.requested_tier,
        supplier_slug: supplierSlug,
        request_id: request.id,
      });
      if (!url) throw new Error('No checkout URL returned.');
      window.location.href = url;
    } catch (e) {
      toast.error(`Payment could not start: ${e?.message || e}`);
      setPayingRequestId(null);
    }
  };

  // Return-from-Stripe poll: detect ?session_id=, poll up to 6× until paid.
  useEffect(() => {
    const sid = searchParams.get('session_id');
    if (!sid || pollingSession === sid) return;
    setPollingSession(sid);
    let cancelled = false;
    (async () => {
      let attempts = 0;
      while (!cancelled && attempts < 6) {
        try {
          const r = await paymentsClient.getStatus(sid);
          if (r.paid) {
            setPaymentBanner({ ok: true, text: 'Payment received. Your verification request has been marked PAID and will be reviewed by GIWAHS.' });
            track(EVENTS.SUPPLIER_VERIFICATION_PAID, { session_id: sid });
            await load();
            break;
          }
          if (r.status === 'expired') {
            setPaymentBanner({ ok: false, text: 'Stripe Checkout session expired. Please try again.' });
            break;
          }
        } catch (e) {
          // continue polling on transient errors
        }
        attempts += 1;
        await new Promise((res) => setTimeout(res, 2000));
      }
      if (!cancelled) {
        // Clean ?session_id from URL once polling completes (success or timeout).
        const next = new URLSearchParams(searchParams);
        next.delete('session_id');
        setSearchParams(next, { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (authLoading || loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="animate-spin text-neutral-500" size={32} />
      </div>
    );
  }

  if (err) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="border border-red-500 bg-red-50 p-6 text-red-900">
          <AlertCircle className="mb-2" size={20} />
          <p className="font-display text-lg">{err}</p>
          <Link to="/factories/verification" className="mt-4 inline-block underline">Read about the verification ladder →</Link>
        </div>
      </div>
    );
  }

  const supplier = data?.supplier || {};
  const requests = data?.requests || [];
  const documents = data?.documents || [];
  const computedScore = data?.computed_score ?? 0;
  const docCountByType = documents.reduce((acc, d) => {
    acc[d.doc_type] = (acc[d.doc_type] || 0) + 1;
    return acc;
  }, {});
  const latestRequest = requests[0];
  const status = supplier.verification_status || 'unverified';

  return (
    <div data-testid="supplier-verification-page" className="mx-auto max-w-6xl px-6 py-10">
      <SEO title="Supplier verification · GIWAHS" description="Upload documents to earn your GIWAHS verification tier." />

      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-black pb-6">
        <div>
          <p className="label-overline text-neutral-500">Supplier verification</p>
          <h1 className="mt-1 font-display text-3xl tracking-tight md:text-4xl">{supplier.name || supplier.slug}</h1>
          <p className="mt-1 font-mono-data text-[11px] uppercase tracking-wider text-neutral-500">{supplier.slug}</p>
        </div>
        <Link to="/factories/verification" className="font-mono-data text-[11px] uppercase tracking-wider underline">
          About the ladder →
        </Link>
      </div>

      {paymentBanner && (
        <div
          data-testid="payment-banner"
          className={`mt-6 border-2 p-4 ${paymentBanner.ok ? 'border-emerald-700 bg-emerald-50 text-emerald-900' : 'border-amber-600 bg-amber-50 text-amber-900'}`}
        >
          <p className="font-display text-base">{paymentBanner.text}</p>
        </div>
      )}

      {/* Pending payment CTA — shown only if latest request is in_review AND paid Gold/Elite tier AND not yet paid */}
      {latestRequest && latestRequest.status === 'in_review' && PAID_TIERS.has(latestRequest.requested_tier) && !latestRequest.paid && (
        <div data-testid="pay-cta-card" className="mt-6 border-2 border-amber-600 bg-amber-50 p-5 text-amber-950">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="label-overline">Payment required to complete this request</p>
              <p className="mt-1 font-display text-lg tracking-tight">
                {latestRequest.requested_tier === 'gold' ? 'Premium Gold — NZ$490 / year' : 'Enterprise Elite — NZ$1,490 / year'}
              </p>
              <p className="mt-1 font-mono-data text-[11px] uppercase tracking-wider opacity-80">
                Payment is taken securely by Stripe. Tier activates only after GIWAHS admin review.
              </p>
            </div>
            <button
              type="button"
              data-testid="pay-now-btn"
              onClick={() => startPayment(latestRequest)}
              disabled={payingRequestId === latestRequest.id}
              className="inline-flex items-center gap-2 border border-amber-900 bg-amber-900 px-5 py-3 font-mono-data text-[11px] uppercase tracking-wider text-amber-50 hover:bg-amber-950 disabled:opacity-60"
            >
              {payingRequestId === latestRequest.id ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</> : <><CreditCard size={14} /> Pay now</>}
            </button>
          </div>
        </div>
      )}

      {/* Current state */}
      <section className="mt-8 grid gap-4 md:grid-cols-3">
        <div data-testid="verif-tier-card" className="border border-black bg-white p-6">
          <div className="label-overline text-neutral-500">Current tier</div>
          <div className="mt-2 flex items-center gap-2">
            <Award size={18} strokeWidth={1.5} />
            <span className="font-display text-2xl tracking-tight">{(supplier.tier || 'unverified').toUpperCase()}</span>
          </div>
          {supplier.last_verified_at && (
            <p className="mt-2 font-mono-data text-[11px] uppercase tracking-wider text-neutral-500">
              Verified {new Date(supplier.last_verified_at).toLocaleDateString()}
              {supplier.verification_expires_at && (
                <> · Expires {new Date(supplier.verification_expires_at).toLocaleDateString()}</>
              )}
            </p>
          )}
        </div>

        <div data-testid="verif-score-card" className="border border-black bg-white p-6">
          <div className="label-overline text-neutral-500">Trust score (computed live)</div>
          <div className="mt-2 font-display text-4xl tracking-tight">{computedScore}<span className="ml-1 font-mono-data text-base text-neutral-500">/100</span></div>
          <div className="mt-3 h-1.5 w-full bg-neutral-200">
            <div className="h-1.5 bg-black" style={{ width: `${computedScore}%` }} />
          </div>
        </div>

        <div data-testid="verif-status-card" className={`border-2 p-6 ${STATUS_TONE[status] || STATUS_TONE.unverified}`}>
          <div className="label-overline opacity-70">Status</div>
          <div className="mt-2 font-display text-xl tracking-tight uppercase">{status.replace('_', ' ')}</div>
          {latestRequest && (
            <p className="mt-2 font-mono-data text-[11px] uppercase tracking-wider opacity-80">
              Last request: {latestRequest.requested_tier} · {new Date(latestRequest.submitted_at).toLocaleDateString()}
            </p>
          )}
          {latestRequest?.reviewer_note && (
            <p className="mt-3 text-sm opacity-90"><strong>Reviewer note:</strong> {latestRequest.reviewer_note}</p>
          )}
        </div>
      </section>

      {/* Documents */}
      <section className="mt-10">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <p className="label-overline text-neutral-500">Step 1 · Documents</p>
            <h2 className="mt-1 font-display text-2xl tracking-tight">Upload your proof</h2>
          </div>
          <p className="font-mono-data text-[11px] uppercase tracking-wider text-neutral-500">
            Max 25 MB · PDF / JPG / PNG / WEBP / HEIC · Private (admin only)
          </p>
        </div>

        <div className="mt-6 border border-black bg-white">
          {DOC_TYPES.filter(d => !d.internalOnly).map((d, i) => {
            const count = docCountByType[d.key] || 0;
            const requiredFor = d.required.join(', ');
            const tone = count > 0 ? 'text-emerald-700' : 'text-neutral-500';
            return (
              <div key={d.key} data-testid={`doc-row-${d.key}`} className={`flex flex-wrap items-center justify-between gap-4 p-5 ${i < DOC_TYPES.length - 1 ? 'border-b border-neutral-200' : ''}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="shrink-0 text-neutral-500" />
                    <p className="font-display text-base tracking-tight">{d.label}</p>
                    {count > 0 && (
                      <span className={`inline-flex items-center gap-1 border border-emerald-700 bg-emerald-50 px-1.5 py-0.5 font-mono-data text-[10px] uppercase tracking-wider text-emerald-900`}>
                        <Check size={10} /> {count} uploaded
                      </span>
                    )}
                  </div>
                  <p className={`mt-1 ml-6 font-mono-data text-[10px] uppercase tracking-wider ${tone}`}>
                    Required for: {requiredFor}
                    {d.recommendedFor && ` · Recommended for: ${d.recommendedFor.join(', ')}`}
                  </p>
                </div>
                <label data-testid={`doc-upload-${d.key}`} className={`inline-flex cursor-pointer items-center gap-1.5 border border-black px-3 py-1.5 font-mono-data text-[10px] uppercase tracking-wider hover:bg-black hover:text-white ${uploadingType === d.key ? 'opacity-50' : ''}`}>
                  {uploadingType === d.key ? (<><Loader2 size={12} className="animate-spin" /> Uploading…</>) : (<><Upload size={12} /> {count > 0 && !d.multi ? 'Replace' : 'Upload'}</>)}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                    className="hidden"
                    disabled={uploadingType === d.key}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleUpload(d.key, f);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            );
          })}
        </div>
      </section>

      {/* Submit request */}
      <section className="mt-10">
        <p className="label-overline text-neutral-500">Step 2 · Submit for review</p>
        <h2 className="mt-1 font-display text-2xl tracking-tight">Pick the tier you're applying for</h2>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {TIER_OPTIONS.map((opt) => (
            <button
              type="button"
              key={opt.key}
              data-testid={`tier-pick-${opt.key}`}
              onClick={() => setRequestedTier(opt.key)}
              className={`border-2 p-5 text-left transition ${requestedTier === opt.key ? 'border-black bg-neutral-900 text-white' : 'border-neutral-300 bg-white hover:border-black'}`}
            >
              <div className="font-display text-xl tracking-tight">{opt.label}</div>
              <div className={`mt-1 font-mono-data text-[10px] uppercase tracking-wider ${requestedTier === opt.key ? 'opacity-80' : 'text-neutral-500'}`}>
                {opt.price}
              </div>
            </button>
          ))}
        </div>

        <div className="mt-6">
          <label htmlFor="attestation" className="label-overline text-neutral-500">Capacity attestation (optional · required for Gold + Elite)</label>
          <textarea
            id="attestation"
            data-testid="verif-attestation"
            rows={4}
            value={attestation}
            onChange={(e) => setAttestation(e.target.value)}
            placeholder="Briefly attest to your monthly production capacity, machinery count, employee headcount, and primary export markets."
            className="mt-2 w-full border border-black bg-white p-3 font-mono-data text-[12px]"
          />
        </div>

        <button
          type="button"
          data-testid="verif-submit-btn"
          onClick={submitForReview}
          disabled={submitting}
          className="mt-6 inline-flex items-center gap-2 border border-black bg-black px-5 py-3 font-mono-data text-[11px] uppercase tracking-wider text-white hover:bg-neutral-800 disabled:opacity-50"
        >
          {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : <>Submit for review <ArrowRight size={14} /></>}
        </button>
        <p className="mt-3 font-mono-data text-[11px] uppercase tracking-wider text-neutral-500">
          Typical review turnaround: 2–3 business days. You'll be emailed at the address on this account.
        </p>
      </section>

      {/* History */}
      {requests.length > 0 && (
        <section className="mt-10">
          <p className="label-overline text-neutral-500">History</p>
          <h2 className="mt-1 font-display text-2xl tracking-tight">Your past requests</h2>
          <div className="mt-4 border border-black bg-white">
            {requests.map((r, i) => (
              <div key={r.id} data-testid={`verif-history-${i}`} className={`flex flex-wrap items-center justify-between gap-4 p-4 ${i < requests.length - 1 ? 'border-b border-neutral-200' : ''}`}>
                <div>
                  <div className="font-display text-base tracking-tight">{r.requested_tier?.toUpperCase()} request</div>
                  <div className="mt-1 font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">
                    <Clock size={10} className="mr-1 inline" /> {new Date(r.submitted_at).toLocaleString()}
                  </div>
                  {r.reviewer_note && <div className="mt-2 text-sm text-neutral-700">Note: {r.reviewer_note}</div>}
                </div>
                <span className={`inline-flex items-center gap-1 border px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider ${
                  r.status === 'approved' ? 'border-emerald-700 bg-emerald-50 text-emerald-900' :
                  r.status === 'rejected' ? 'border-red-500 bg-red-50 text-red-900' :
                  r.status === 'in_review' ? 'border-amber-500 bg-amber-50 text-amber-900' :
                  'border-neutral-300 bg-neutral-50 text-neutral-700'
                }`}>
                  {r.status === 'approved' && <Check size={10} />}
                  {r.status === 'rejected' && <X size={10} />}
                  {r.status === 'in_review' && <Loader2 size={10} />}
                  {r.status}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default SupplierVerification;
